/**
 * A photo as the engine should see it (OPS-1): decoded, and no longer than
 * SCAN_MAX_SIDE on its long side. A 12-megapixel capture is slow to OCR and
 * reads no better than a 1,600 px one; a file that is not an image at all is
 * refused here, with its own error, before any engine starts.
 */

export const SCAN_MAX_SIDE = 1600

/** A file that decodes as no image. */
export class InvalidImage extends Error {
  constructor() {
    super('the file is not an image')
    this.name = 'InvalidImage'
  }
}

/** The size within `max` on the long side, the ratio kept; never enlarged. */
export function fitWithin(
  width: number,
  height: number,
  max = SCAN_MAX_SIDE,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= max || longest === 0) return { width, height }
  const scale = max / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/** Decode and downscale a captured or chosen file to a JPEG the engine reads. */
export async function prepareImage(file: Blob, max = SCAN_MAX_SIDE): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new InvalidImage()
  }
  try {
    const size = fitWithin(bitmap.width, bitmap.height, max)
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context to prepare the image')
    ctx.drawImage(bitmap, 0, 0, size.width, size.height)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92),
    )
    if (!blob) throw new Error('the image could not be encoded')
    return blob
  } finally {
    bitmap.close()
  }
}
